import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Pagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Snackbar,
  Chip,
  Menu,
  MenuItem
} from '@mui/material';
import { Edit, Delete, Add, MoreVert, Visibility, Close } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import hu from 'date-fns/locale/hu';
import blogPostService from '../../services/blogPostService';
import { useNavigate } from 'react-router-dom';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';
import { useConfirm } from '../Common/ConfirmDialog';

const AdminBlogPostsPage = () => {
  const [confirm, confirmDialog] = useConfirm();
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [searchTerm, setSearchTerm] = useState('');
  const [isPublishedFilter, setIsPublishedFilter] = useState('ALL'); // ALL, true, false

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [currentBlogPost, setCurrentBlogPost] = useState(null); // null for create, object for edit
  const [formValues, setFormValues] = useState({
    title: '',
    content: '',
    publishedAt: '',
    isPublished: false,
    imageUrl: '',
    metaDescription: '',
    tags: '', // string, will be split by comma
  });
  const [formErrors, setFormErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  // Snackbar for messages
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Action Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [selectedBlogPostForMenu, setSelectedBlogPostForMenu] = useState(null);

  const fetchBlogPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        page,
        limit,
        search: searchTerm,
      };
      if (isPublishedFilter !== 'ALL') {
        filters.isPublished = isPublishedFilter;
      }

      const response = await blogPostService.getBlogPosts(filters);
      if (response.success) {
        setBlogPosts(response.data);
        setPagination(response.pagination);
      } else {
        setError(response.message || 'Blogbejegyzések betöltése sikertelen.');
      }
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Hiba történt a blogbejegyzések betöltése során.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, isPublishedFilter]);

  useEffect(() => {
    fetchBlogPosts();
  }, [fetchBlogPosts]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset page on search
  };

  const handleIsPublishedFilterChange = (event) => {
    setIsPublishedFilter(event.target.value);
    setPage(1); // Reset page on filter change
  };

  const handleOpenCreateDialog = () => {
    setCurrentBlogPost(null);
    setFormValues({
      title: '',
      content: '',
      publishedAt: '',
      isPublished: false,
      imageUrl: '',
      metaDescription: '',
      tags: '',
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (blogPost) => {
    setCurrentBlogPost(blogPost);
    setFormValues({
      title: blogPost.title,
      content: blogPost.content,
      publishedAt: blogPost.publishedAt ? format(parseISO(blogPost.publishedAt), 'yyyy-MM-dd') : '',
      isPublished: blogPost.isPublished,
      imageUrl: blogPost.imageUrl || '',
      metaDescription: blogPost.metaDescription || '',
      tags: blogPost.tags ? blogPost.tags.join(', ') : '',
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formValues.title) errors.title = 'A cím kötelező.';
    if (!formValues.content) errors.content = 'A tartalom kötelező.';
    if (formValues.imageUrl && !/^https?:\/\/.+\..+/.test(formValues.imageUrl)) errors.imageUrl = 'Érvénytelen URL formátum.';
    if (formValues.metaDescription && formValues.metaDescription.length > 255) errors.metaDescription = 'A meta leírás maximum 255 karakter lehet.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setActionLoading(true);
    try {
      const dataToSend = {
        ...formValues,
        tags: formValues.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
        publishedAt: formValues.publishedAt ? new Date(formValues.publishedAt).toISOString() : null,
      };

      let response;
      if (currentBlogPost) {
        response = await blogPostService.updateBlogPost(currentBlogPost.id, dataToSend);
      } else {
        response = await blogPostService.createBlogPost(dataToSend);
      }

      if (response.success) {
        showSnackbar(response.message, 'success');
        fetchBlogPosts();
        handleCloseDialog();
      } else {
        showSnackbar(response.message || 'Hiba történt a művelet során.', 'error');
      }
    } catch (err) {
      console.error('Submit error:', err);
      showSnackbar(err.message || 'Szerver hiba.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Blogbejegyzés törlése', message: 'Biztosan törölni szeretné ezt a blogbejegyzést?', confirmLabel: 'Törlés', color: 'error' }))) {
      return;
    }
    setActionLoading(true);
    try {
      const response = await blogPostService.deleteBlogPost(id);
      if (response.success) {
        showSnackbar(response.message, 'success');
        fetchBlogPosts();
      } else {
        showSnackbar(response.message || 'Hiba történt a törlés során.', 'error');
      }
    } catch (err) {
      console.error('Delete error:', err);
      showSnackbar(err.message || 'Szerver hiba.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleMenuClick = (event, blogPost) => {
    setAnchorEl(event.currentTarget);
    setSelectedBlogPostForMenu(blogPost);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBlogPostForMenu(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Blogbejegyzések kezelése
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <TextField
          label="Keresés"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ width: '300px' }}
        />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreateDialog}
        >
          Új bejegyzés
        </Button>
      </Box>

      <ResponsiveTableContainer stickyLastColumn>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Cím</TableCell>
              <TableCell>Szerző</TableCell>
              <TableCell>Publikálva</TableCell>
              <TableCell>Publikálás dátuma</TableCell>
              <TableCell>Létrehozva</TableCell>
              <TableCell align="center">Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blogPosts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nincsenek blogbejegyzések.
                </TableCell>
              </TableRow>
            ) : (
              blogPosts.map((post) => (
                <TableRow key={post.id}>
                  <TableCell>{post.id}</TableCell>
                  <TableCell>{post.title}</TableCell>
                  <TableCell>{post.author?.firstName} {post.author?.lastName}</TableCell>
                  <TableCell>
                    <Chip
                      label={post.isPublished ? 'Igen' : 'Nem'}
                      color={post.isPublished ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {post.publishedAt ? format(parseISO(post.publishedAt), 'yyyy. MM. dd.', { locale: hu }) : 'Nincs'}
                  </TableCell>
                  <TableCell>{format(parseISO(post.createdAt), 'yyyy. MM. dd.', { locale: hu })}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={(event) => handleMenuClick(event, post)}>
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={openMenu && selectedBlogPostForMenu?.id === post.id}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => { handleMenuClose(); handleOpenEditDialog(post); }}>
                        <Edit fontSize="small" sx={{ mr: 1 }} /> Szerkesztés
                      </MenuItem>
                      <MenuItem onClick={() => { handleMenuClose(); handleDelete(post.id); }}>
                        <Delete fontSize="small" sx={{ mr: 1 }} /> Törlés
                      </MenuItem>
                      <MenuItem onClick={() => { handleMenuClose(); navigate(`/blog/${post.slug}`); }}>
                        <Visibility fontSize="small" sx={{ mr: 1 }} /> Megtekintés
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {pagination.pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 2 }}>
          <Pagination
            count={pagination.pages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{currentBlogPost ? 'Blogbejegyzés szerkesztése' : 'Új blogbejegyzés létrehozása'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Cím"
            type="text"
            fullWidth
            variant="outlined"
            value={formValues.title}
            onChange={handleChange}
            error={!!formErrors.title}
            helperText={formErrors.title}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="content"
            label="Tartalom (HTML/Markdown)"
            multiline
            rows={10}
            fullWidth
            variant="outlined"
            value={formValues.content}
            onChange={handleChange}
            error={!!formErrors.content}
            helperText={formErrors.content}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="imageUrl"
            label="Kiemelt kép URL-je"
            type="url"
            fullWidth
            variant="outlined"
            value={formValues.imageUrl}
            onChange={handleChange}
            error={!!formErrors.imageUrl}
            helperText={formErrors.imageUrl}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="metaDescription"
            label="Meta leírás (SEO)"
            multiline
            rows={2}
            fullWidth
            variant="outlined"
            value={formValues.metaDescription}
            onChange={handleChange}
            inputProps={{ maxLength: 255 }}
            error={!!formErrors.metaDescription}
            helperText={`${formValues.metaDescription.length}/255 ${formErrors.metaDescription || ''}`}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="tags"
            label="Címkék (vesszővel elválasztva)"
            type="text"
            fullWidth
            variant="outlined"
            value={formValues.tags}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="publishedAt"
            label="Publikálás dátuma (opcionális)"
            type="date"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={formValues.publishedAt}
            onChange={handleChange}
            error={!!formErrors.publishedAt}
            helperText={formErrors.publishedAt}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formValues.isPublished}
                onChange={handleChange}
                name="isPublished"
                color="primary"
              />
            }
            label="Publikálva"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={actionLoading}>
            Mégse
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : null}
          >
            {currentBlogPost ? 'Mentés' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setSnackbarOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {confirmDialog}
    </Box>
  );
};

export default AdminBlogPostsPage;